import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { listUsers, updateUser, deleteUser, type AdminUser } from '../../api/users';
import { fetchStructure } from '../../api/structure';
import { ApiError } from '../../api/client';
import { TABS, type TabId } from '../../tabs';
import type { Role } from '../../api/auth';
import { palette } from '../../theme';

export function UsersAdminPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [error, setError] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: ({ signal }) => listUsers(signal).then((r) => r.users),
  });

  const structureQuery = useQuery({
    queryKey: ['structure'],
    queryFn: ({ signal }) => fetchStructure(signal),
  });
  const availableLayers = structureQuery.data?.layers.map((l) => l.layer) ?? [];

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { role?: Role; tabs?: TabId[]; sections?: string[]; managerId?: string | null } }) =>
      updateUser(id, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
    onError: (err: unknown) => setError(err instanceof ApiError ? err.message : 'Update failed.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
    onError: (err: unknown) => setError(err instanceof ApiError ? err.message : 'Delete failed.'),
  });

  const users = usersQuery.data ?? [];
  const usersById = new Map(users.map((u) => [u.id, u]));

  if (usersQuery.isLoading) {
    return (
      <Box sx={{ py: 6, display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="end" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage roles and per-section dashboard access. Non-admins see only the sections you assign; admins see everything. The first signed-in user is admin.
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Access</TableCell>
                <TableCell>Manager</TableCell>
                <TableCell>Last login</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usersQuery.data?.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar src={u.picture} sx={{ width: 32, height: 32 }}>
                        {u.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {u.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {u.email}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={u.role}
                      sx={{
                        textTransform: 'capitalize',
                        bgcolor: u.role === 'admin' ? palette.sage[100] : 'grey.100',
                        color: u.role === 'admin' ? palette.sage[700] : 'text.primary',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {u.role === 'admin' ? (
                      <Typography variant="caption" color="text.secondary">
                        all (admin)
                      </Typography>
                    ) : u.tabs.length === 0 && u.sections.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        none
                      </Typography>
                    ) : (
                      <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {u.sections.map((s) => (
                          <Chip
                            key={s}
                            size="small"
                            label={s}
                            variant="outlined"
                            sx={{ borderColor: palette.sage[200], bgcolor: palette.sage[50] }}
                          />
                        ))}
                        {u.tabs.map((t) => (
                          <Chip key={t} size="small" label={t} variant="outlined" />
                        ))}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {u.managerId ? usersById.get(u.managerId)?.name ?? 'Unknown' : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" gap={0.5} justifyContent="end">
                      <Button
                        size="small"
                        startIcon={<EditRoundedIcon />}
                        onClick={() => setEditing(u)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteRoundedIcon />}
                        onClick={() => {
                          if (confirm(`Delete ${u.email}?`)) deleteMutation.mutate(u.id);
                        }}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <EditUserDialog
        user={editing}
        allUsers={users}
        availableLayers={availableLayers}
        onClose={() => setEditing(null)}
        onSubmit={(body) => {
          if (!editing) return;
          updateMutation.mutate(
            { id: editing.id, body },
            { onSuccess: () => setEditing(null) },
          );
        }}
        submitting={updateMutation.isPending}
      />
    </Box>
  );
}

function EditUserDialog({
  user,
  allUsers,
  availableLayers,
  onClose,
  onSubmit,
  submitting,
}: {
  user: AdminUser | null;
  allUsers: AdminUser[];
  availableLayers: string[];
  onClose: () => void;
  onSubmit: (body: { role: Role; tabs: TabId[]; sections: string[]; managerId: string | null }) => void;
  submitting: boolean;
}) {
  const [role, setRole] = useState<Role>('viewer');
  const [tabs, setTabs] = useState<TabId[]>([]);
  const [sections, setSections] = useState<string[]>([]);
  const [managerId, setManagerId] = useState<string>('');

  const handleEnter = () => {
    if (user) {
      setRole(user.role);
      setTabs(user.tabs);
      setSections(user.sections ?? []);
      setManagerId(user.managerId ?? '');
    }
  };

  const managerOptions = allUsers.filter((u) => u.id !== user?.id);

  // Union of the available layers and any already-granted ones, so a grant for a
  // layer no longer in the sheet stays visible (and removable) in the dialog.
  const layerOptions = Array.from(new Set([...availableLayers, ...(user?.sections ?? [])]));

  const toggle = (id: TabId) => {
    setTabs((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
  };

  const toggleSection = (layer: string) => {
    setSections((prev) => (prev.includes(layer) ? prev.filter((s) => s !== layer) : [...prev, layer]));
  };

  return (
    <Dialog open={!!user} onClose={onClose} TransitionProps={{ onEnter: handleEnter }} maxWidth="sm" fullWidth>
      <DialogTitle>Edit {user?.email}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select label="Role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="editor">Editor</MenuItem>
              <MenuItem value="viewer">Viewer</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Manager</InputLabel>
            <Select label="Manager" value={managerId} onChange={(e) => setManagerId(e.target.value)}>
              <MenuItem value="">
                <em>— none —</em>
              </MenuItem>
              {managerOptions.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name} ({m.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Section access
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Grant the dashboard sections this user may see. Admins see all sections; users see only what you assign here. Runs & Health is a separate operational section.
            </Typography>
            <FormGroup>
              {role === 'admin' ? (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                  Admins can see every dashboard section.
                </Typography>
              ) : layerOptions.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                  No dashboard sections available yet. Run the sync to populate Layers.
                </Typography>
              ) : (
                layerOptions.map((layer) => (
                  <FormControlLabel
                    key={layer}
                    control={<Checkbox checked={sections.includes(layer)} onChange={() => toggleSection(layer)} />}
                    label={<Typography variant="body2">{layer}</Typography>}
                  />
                ))
              )}
            </FormGroup>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, mb: 0.5, display: 'block' }}>
              Operational section
            </Typography>
            <FormGroup>
              {TABS.map((t) => (
                <FormControlLabel
                  key={t.id}
                  control={<Checkbox checked={tabs.includes(t.id)} onChange={() => toggle(t.id)} />}
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{t.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({t.id})
                      </Typography>
                    </Stack>
                  }
                />
              ))}
            </FormGroup>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={submitting}
          onClick={() => onSubmit({ role, tabs, sections, managerId: managerId || null })}
        >
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
